import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { C } from '../theme';

const pageSx = {
  pt: '110px',
  pb: 8,
  minHeight: '100vh',
  backgroundColor: C.bg,
  backgroundImage: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(243,192,21,0.10), transparent 70%)',
};
const paperSx = { p: { xs: 3, md: 5 }, backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}` };

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSuccess(true);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Greška pri slanju email-a');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={pageSx}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: C.white, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Zaboravili ste lozinku?
          </Typography>
          <Typography variant="h6" sx={{ color: C.muted, maxWidth: 500, mx: 'auto', lineHeight: 1.6, fontWeight: 400, fontSize: '1.05rem' }}>
            Unesite vašu email adresu i poslaćemo vam link za resetovanje lozinke
          </Typography>
        </Box>

        <Paper elevation={0} sx={paperSx}>
          {!success ? (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 4 }}>
                  {error}
                </Alert>
              )}

              <TextField
                required
                fullWidth
                label="Email adresa"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="unesite@email.com"
                InputProps={{ startAdornment: <EmailIcon sx={{ color: C.muted, mr: 1 }} /> }}
                sx={{ mb: 4 }}
              />

              <Button type="submit" fullWidth variant="contained" disabled={loading || !email} sx={{ py: 1.5, mb: 3, fontSize: '1.05rem' }}>
                {loading ? <CircularProgress size={24} sx={{ color: C.onGold }} /> : 'Pošaljite link za resetovanje'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link to="/login" style={{ textDecoration: 'none', color: '#F6CB4B', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <ArrowBackIcon fontSize="small" />
                  Nazad na prijavu
                </Link>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <EmailIcon sx={{ fontSize: 80, color: C.gold, mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
                Email poslat!
              </Typography>
              <Typography variant="body1" sx={{ color: C.muted, mb: 4, lineHeight: 1.6 }}>
                Ako postoji nalog sa ovom email adresom, poslali smo vam link za resetovanje lozinke.
                Proverite vašu email poštu i spam folder.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')} sx={{ px: 4, py: 1.5 }}>
                Nazad na prijavu
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
